import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { formatCurrency } from "@/lib/currency";
import { readJsonArrayOrEmpty } from "@/lib/http";
import { sortSalesNewestFirst } from "@/lib/sales-order";
import { DashboardMoneyToggle, useDashboardMoneyVisibility } from "./dashboard-money-visibility";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingCart, BarChart3, TrendingUp, Receipt,
  Clock, ArrowRight, Utensils, Banknote, AlertTriangle,
  Users, FlameKindling, TrendingDown,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string; icon: React.ElementType; color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function FoodDashboard() {
  const { user, store } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const branchQuery = activeBranchId ? `?branchId=${activeBranchId}` : "";
  const isOwner = user?.role === "admin" || user?.role === "superadmin";
  const isManager = user?.role === "manager" || isOwner;
  const { moneyVisible, toggleMoneyVisibility, maskMoney } = useDashboardMoneyVisibility();

  const [stats, setStats] = useState({
    todayRevenue: 0, todayPlates: 0,
    todayExpenses: 0, netToday: 0,
    todayCash: 0, todayTransfer: 0,
    menuItems: 0, soldOutItems: 0,
    activeStaff: 0,
  });
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [soldOut, setSoldOut] = useState<any[]>([]);
  const [todayOrders, setTodayOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lagosDate = (value: Date | string) => new Date(value).toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
        const today = lagosDate(new Date());
        const [salesRes, productsRes, expensesRes, staffRes] = await Promise.allSettled([
          fetch(`${BASE}/api/sales${branchQuery}`, { credentials: "include", cache: "no-store" }),
          fetch(`${BASE}/api/products${branchQuery}`, { credentials: "include" }),
          fetch(`${BASE}/api/expenses${branchQuery}`, { credentials: "include" }),
          fetch(`${BASE}/api/staff${branchQuery}`, { credentials: "include" }),
        ]);
        const sales = salesRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(salesRes.value) : [];
        const products = productsRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(productsRes.value) : [];
        const expenses = expensesRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(expensesRes.value) : [];
        const staff = staffRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(staffRes.value) : [];

        const newestSales = sortSalesNewestFirst(sales);
        const todaySales = newestSales.filter((s: any) => lagosDate(s.createdAt) === today);
        const todayExp = expenses.filter((e: any) => (e.date || "").startsWith(today));
        const activeItems = products as any[];
        const outItems = activeItems.filter((p: any) => p.stock === 0);

        const todayRev = todaySales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0);
        const todayExpAmt = todayExp.reduce((s: number, x: any) => s + Number(x.amount || 0), 0);
        const todayCash = todaySales
          .filter((s: any) => (s.paymentMethod || "cash").toLowerCase() === "cash")
          .reduce((a: number, s: any) => a + Number(s.totalAmount ?? s.total ?? 0), 0);
        const todayTransfer = todayRev - todayCash;

        setStats({
          todayRevenue: todayRev,
          todayPlates: todaySales.length,
          todayExpenses: todayExpAmt,
          netToday: todayRev - todayExpAmt,
          todayCash, todayTransfer,
          menuItems: activeItems.length,
          soldOutItems: outItems.length,
          activeStaff: (staff as any[]).filter((s: any) => s.isActive).length,
        });
        // Sort: sold out last in list, rest sorted by stock ascending (finishing items first)
        setMenuItems(
          activeItems
            .sort((a: any, b: any) => a.stock - b.stock)
            .slice(0, 8)
        );
        setSoldOut(outItems.slice(0, 4));
        setTodayOrders(todaySales.slice(0, 6));
      } finally { setLoading(false); }
    };
    load();
  }, [branchQuery]);

  const netPositive = stats.netToday >= 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">🍽️</span>
            <h1 className="text-2xl font-bold">{store?.name || "My Buka"}</h1>
            <Badge className="bg-red-100 text-red-700 border-red-200">Food · Buka · Frozen & Fresh</Badge>
            <Badge variant="outline" className="capitalize">{user?.role}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            Meals, Frozen & Fresh Food Dashboard · {activeBranch?.name ?? "All Branches"} · {new Date().toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DashboardMoneyToggle visible={moneyVisible} onToggle={toggleMoneyVisibility} />
          {isManager && (
            <Link href="/expenses?add=1">
              <Button variant="outline" size="sm" className="gap-1.5"><Receipt className="w-3.5 h-3.5" />Log Expense</Button>
            </Link>
          )}
          <Link href="/sales/new">
            <Button size="sm" className="gap-1.5 bg-red-600 hover:bg-red-700"><Utensils className="w-3.5 h-3.5" />Serve Customer</Button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Today's Sales"
          value={maskMoney(formatCurrency(stats.todayRevenue))}
          icon={TrendingUp}
          color="bg-red-100 text-red-600"
          sub={`${stats.todayPlates} sale${stats.todayPlates !== 1 ? "s" : ""} — plates, packs & portions`}
        />
        <StatCard
          label="Expenses Today"
          value={maskMoney(formatCurrency(stats.todayExpenses))}
          icon={FlameKindling}
          color="bg-orange-100 text-orange-600"
          sub="ingredients, restock, gas & running costs"
        />
        <StatCard
          label="Net Profit Today"
          value={maskMoney(formatCurrency(stats.netToday))}
          icon={netPositive ? TrendingUp : TrendingDown}
          color={netPositive ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}
          sub={moneyVisible ? netPositive ? "You're in profit today" : "Expenses exceed sales" : "####"}
        />
        <StatCard
          label="Menu / Stock Items"
          value={String(stats.menuItems)}
          icon={stats.soldOutItems > 0 ? AlertTriangle : Utensils}
          color={stats.soldOutItems > 0 ? "bg-amber-100 text-amber-600" : "bg-blue-100 text-blue-600"}
          sub={stats.soldOutItems > 0 ? `${stats.soldOutItems} item${stats.soldOutItems !== 1 ? "s" : ""} sold out` : `${stats.activeStaff} staff on duty`}
        />
      </div>

      {/* Sold-out alert */}
      {soldOut.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">Items finished / sold out today</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {soldOut.map((p: any) => p.name).join(" · ")}
            </p>
          </div>
          <Link href="/inventory">
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs flex-shrink-0">Restock</Button>
          </Link>
        </div>
      )}

      {/* Cash breakdown */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Banknote className="w-5 h-5 text-green-700" />
          </div>
          <div>
            <div className="text-xl font-bold text-green-800">{maskMoney(formatCurrency(stats.todayCash))}</div>
            <div className="text-xs text-green-700 font-medium">Cash Collected Today</div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <div className="text-xl font-bold text-blue-800">{maskMoney(formatCurrency(stats.todayTransfer))}</div>
            <div className="text-xs text-blue-700 font-medium">Transfer / POS Today</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Serve Customer",  href: "/sales/new",       icon: ShoppingCart,  color: "bg-red-50 border-red-200 hover:bg-red-100"      },
            { label: "Today's Orders",  href: "/sales",           icon: BarChart3,     color: "bg-orange-50 border-orange-200 hover:bg-orange-100"},
            { label: "Log Expense",     href: "/expenses?add=1",  icon: Receipt,       color: "bg-amber-50 border-amber-200 hover:bg-amber-100", mgr: true },
            { label: "Staff Rota",      href: "/attendance",      icon: Clock,         color: "bg-blue-50 border-blue-200 hover:bg-blue-100",   mgr: true },
          ].filter((a: any) => !a.mgr || isManager).map(a => (
            <Link key={a.label} href={a.href}>
              <div className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors text-slate-800 ${a.color}`}>
                <a.icon className="w-5 h-5 text-slate-700" />
                <span className="text-sm font-semibold">{a.label}</span>
                <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-500" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Menu / Stock — sorted finishing first */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Utensils className="w-4 h-4 text-red-500" />Meals, Frozen & Fresh Stock
            </h3>
            <Link href="/inventory"><span className="text-xs text-primary hover:underline cursor-pointer">Manage →</span></Link>
          </div>
          {menuItems.length === 0 ? (
            <div className="text-center py-6">
              <Utensils className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Add cooked meals, frozen goods, or fresh produce</p>
              <Link href="/inventory?add=1">
                <Button size="sm" className="mt-3 bg-red-600 hover:bg-red-700">Add First Product</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {menuItems.map((item: any) => {
                const isOut = item.stock === 0;
                const isLow = !isOut && item.stock <= (item.reorderLevel ?? 3);
                return (
                  <div key={item.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isOut ? "bg-red-500" : isLow ? "bg-amber-500" : "bg-emerald-500"}`} />
                      <span className="text-sm font-medium truncate">{item.name}</span>
                      {isOut && <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] px-1.5 py-0 flex-shrink-0">Finished</Badge>}
                      {isLow && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0 flex-shrink-0">Running Low</Badge>}
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <div className="text-sm font-semibold">{formatCurrency(Number(item.price))}</div>
                      {!isOut && <div className="text-xs text-muted-foreground">{item.stock} left</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Today's Orders */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-orange-500" />Today's Orders
            </h3>
            <Link href="/sales"><span className="text-xs text-primary hover:underline cursor-pointer">View all →</span></Link>
          </div>
          {todayOrders.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No orders recorded today yet</p>
              <Link href="/sales/new">
                <Button size="sm" className="mt-3 bg-red-600 hover:bg-red-700">Take First Order</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {todayOrders.map((s: any) => {
                const isCash = (s.paymentMethod || "cash").toLowerCase() === "cash";
                const time = new Date(s.createdAt).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" });
                return (
                  <div key={s.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div>
                      <div className="text-sm font-medium">{s.customerName ?? "Counter Order"}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 py-0 ${isCash ? "text-green-700 border-green-300" : "text-blue-700 border-blue-300"}`}
                        >
                          {isCash ? "Cash" : s.paymentMethod || "Transfer"}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{time}</span>
                      </div>
                    </div>
                    <div className="text-sm font-semibold">{maskMoney(formatCurrency(Number(s.totalAmount ?? s.total ?? 0)))}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {isOwner && (
        <div className="bg-muted/40 border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-red-500" />Owner Analytics
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Profit & Loss",       href: "/accounting/profit-loss" },
              { label: "Daily Cash Flow",      href: "/accounting/cash-flow" },
              { label: "Expense Reports",      href: "/accounting/expense-reports" },
              { label: "Staff Performance",    href: "/people/staff-performance" },
            ].map(l => (
              <Link key={l.label} href={l.href}>
                <Button variant="outline" size="sm" className="w-full text-xs">{l.label}</Button>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
